/**
 * UX-GROWTH X5 (first step): an on-device draft of a new order or quote, so a reload, crash, lost
 * connection or session timeout doesn't lose the lines. Only what's needed to rebuild the order is
 * kept (item, description, quantity, unit, ship-to, ship-from) — never prices, which are re-read from
 * the catalogue on restore. Per person + customer + mode; dropped after 7 days and on sign-out.
 */
export const ORDER_DRAFT_PREFIX = 'bcportal.orderDraft.v1:';
export const ORDER_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface OrderDraftLine { itemNo: string; description?: string; quantity: number; unitOfMeasure?: string }
export interface OrderDraft { savedAt: string; lines: OrderDraftLine[]; shipToCode?: string; locationCode?: string }

export const orderDraftKey = (who: string, customer: string, mode: 'order' | 'quote'): string =>
  `${ORDER_DRAFT_PREFIX}${who || 'anon'}:${customer || 'none'}:${mode}`;

type DraftInputLine = { itemNo?: string; description?: string; quantity?: number; unitOfMeasure?: string };

export function saveOrderDraft(key: string, draft: { lines: DraftInputLine[]; shipToCode?: string; locationCode?: string }, now = new Date()): void {
  try {
    const lines = draft.lines
      .filter(l => l && typeof l.itemNo === 'string' && l.itemNo)
      .map(l => ({ itemNo: l.itemNo as string, description: l.description, quantity: Number(l.quantity) || 0, unitOfMeasure: l.unitOfMeasure }));
    if (lines.length === 0) { localStorage.removeItem(key); return; }
    localStorage.setItem(key, JSON.stringify({ savedAt: now.toISOString(), lines, shipToCode: draft.shipToCode || undefined, locationCode: draft.locationCode || undefined }));
  } catch { /* storage full or blocked: drafts are best-effort */ }
}

export function loadOrderDraft(key: string, now = Date.now()): OrderDraft | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) { return null; }
    const d = JSON.parse(raw) as OrderDraft;
    const age = now - Date.parse(d.savedAt);
    if (!Array.isArray(d.lines) || d.lines.length === 0 || !(age >= 0) || age > ORDER_DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return d;
  } catch {
    return null;
  }
}

export function clearOrderDraft(key: string): void {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** Sign-out: drafts stay on this device only while someone is signed in (shared warehouse terminals). */
export function clearAllOrderDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k?.startsWith(ORDER_DRAFT_PREFIX)) { keys.push(k); } }
    keys.forEach(k => localStorage.removeItem(k));
  } catch { /* ignore */ }
}
